import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip, Chip, CircularProgress } from '@mui/material';
import MUIDataTable from "mui-datatables";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEnvelope, faCheckCircle, faBan } from '@fortawesome/free-solid-svg-icons';

// Asumo que tienes estas funciones exportadas en tu controlador frontend
import { 
    getEventTickets, 
    validateTicket, 
    annulTicket, 
    resendTicketEmail 
} from '../../../../controllers/company/events/MerchantTicketController';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';

const EventTicketsTab = ({ eventId }) => {
    const [tickets, setTickets] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (eventId) fetchTickets();
    }, [eventId]);

    const fetchTickets = async () => {
        setIsLoading(true);
        const response = await getEventTickets(eventId);
        if (response.success) {
            setTickets(response.data);
        }
        setIsLoading(false);
    };

    // ==========================================
    // LÓGICA DE ACCIONES REALES
    // ==========================================
    const handleValidate = async (ticket) => {
        if (!window.confirm(`¿Seguro que deseas VALIDAR el boleto de ${ticket.customer_name}?`)) return;
        
        const res = await validateTicket(ticket.id);
        if (res.success) {
            // Actualizamos solo este ticket en el estado para no recargar todo
            setTickets(tickets.map(t => t.id === ticket.id ? { ...t, status_code: 2, status_label: 'Validado', status_color: 'info' } : t));
        } else {
            alert(res.message);
        }
    };

    const handleAnnul = async (ticket) => {
        if (!window.confirm(`¿ATENCIÓN! ¿Seguro que deseas ANULAR este boleto de ${ticket.customer_name}? Esta acción es irreversible.`)) return;
        
        const res = await annulTicket(ticket.id);
        if (res.success) {
            setTickets(tickets.map(t => t.id === ticket.id ? { ...t, status_code: 3, status_label: 'Anulado', status_color: 'error' } : t));
        } else {
            alert(res.message);
        }
    };

    const handleResend = async (ticket) => {
        if (!window.confirm(`¿Reenviar correo con el boleto a ${ticket.customer_name}?`)) return;
        
        const response = await resendTicketEmail(ticket.id);
        alert(response.message); // Muestra "Correo enviado exitosamente"
    };

    const handleView = (ticket) => {
        // Aquí puedes abrir tu modal o redirigir a la vista de impresión del PDF
        window.open(`https://qud.life/ticketing/${ticket.uuid}`, '_blank');
    };

    // ==========================================
    // CONFIGURACIÓN DE MUIDATATABLE
    // ==========================================
    const columns = [
        {
            name: "uuid",
            label: "Localizador",
            options: { customBodyRender: (val) => <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{val.substring(0, 8)}</Typography> }
        },
        { name: "order_code", label: "Cod. Pedido", options: { customBodyRender: (val) => <Typography variant="body2" color="primary" fontWeight="bold">{val}</Typography> } },
        {
            name: "customer_name",
            label: "Cliente",
            options: {
                customBodyRenderLite: (dataIndex) => {
                    const ticket = tickets[dataIndex];
                    return (
                        <>
                            {ticket.customer_name}
                            {ticket.is_complimentary && <Chip label="Cortesía" size="small" color="secondary" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />}
                        </>
                    );
                }
            }
        },
        { name: "location", label: "Ubicación" },
        { name: "operator_name", label: "Procesado por" },
        {
            name: "status_label",
            label: "Estatus",
            options: {
                customBodyRenderLite: (dataIndex) => {
                    const t = tickets[dataIndex];
                    return <Chip label={t.status_label} color={t.status_color} size="small" sx={{ textTransform: 'uppercase', fontWeight: 'bold', fontSize: '0.65rem' }} />;
                }
            }
        },
        { name: "created_at", label: "Fecha Emisión" },
        {
            name: "Acciones",
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex) => {
                    const ticket = tickets[dataIndex];
                    return (
                        <Box display="flex" justifyContent="flex-end" gap={1}>
                            <Tooltip title="Ver/Imprimir">
                                <IconButton size="small" href={ticket.customer_phone} target='_blank'><FontAwesomeIcon icon={faWhatsapp} size="xs" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Ver/Imprimir">
                                <IconButton size="small" onClick={() => handleView(ticket)}><FontAwesomeIcon icon={faEye} size="xs" /></IconButton>
                            </Tooltip>
                            
                            {ticket.status_code !== 3 && (
                                <Tooltip title="Reenviar Correo">
                                    <IconButton size="small" onClick={() => handleResend(ticket)}><FontAwesomeIcon icon={faEnvelope} size="xs" /></IconButton>
                                </Tooltip>
                            )}

                            {ticket.status_code === 1 && (
                                <>
                                    <Tooltip title="Validar Entrada (Check-in)">
                                        <IconButton size="small" color="success" onClick={() => handleValidate(ticket)}><FontAwesomeIcon icon={faCheckCircle} size="xs" /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Anular Boleto">
                                        <IconButton size="small" color="error" onClick={() => handleAnnul(ticket)}><FontAwesomeIcon icon={faBan} size="xs" /></IconButton>
                                    </Tooltip>
                                </>
                            )}
                        </Box>
                    );
                }
            }
        }
    ];

    const options = {
        filterType: 'dropdown',
        responsive: 'standard',
        selectableRows: 'none', // Quitamos los checkboxes laterales
        elevation: 1,
        textLabels: {
            body: { noMatch: "No se encontraron boletos", toolTip: "Ordenar" },
            pagination: { next: "Siguiente", previous: "Anterior", rowsPerPage: "Filas por página:", displayRows: "de" },
            toolbar: { search: "Buscar", downloadCsv: "Descargar CSV", print: "Imprimir", viewColumns: "Ver Columnas", filterTable: "Filtrar Tabla" },
            filter: { all: "Todos", title: "FILTROS", reset: "REINICIAR" },
            viewColumns: { title: "Mostrar Columnas", titleAria: "Mostrar/Ocultar Columnas" },
        }
    };

    if (isLoading) return <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>;

    return (
        <Box>
            <MUIDataTable
                title={<Typography variant="h6" fontWeight="bold">Listado de Boletos Emitidos</Typography>}
                data={tickets}
                columns={columns}
                options={options}
            />
        </Box>
    );
};

export default EventTicketsTab;